import ApiService from "./ApiService";
import Logger from "./Logger";

const StatsJigsawService = {
    /**
     * GET /v1/stats/jigsaw/user-plays?start=&end=&language=&includeTest=false&excludeStaff=true
     * Player activity in a window: distinct players and guest sessions, plays per day, the board
     * sizes / difficulties played, and the most played stories. Start / end are epoch seconds.
     * @returns {Promise<{ totals: Object, daily: Array, byDifficulty: Array, topPuzzles: Array }>}
     */
    userPlayStats: async ({
        start,
        end,
        language = "english",
        includeTest = false,
        excludeStaff = true,
    } = {}) => {
        try {
            // ApiService drops falsy values from the query string — send booleans as explicit strings.
            const params = {
                includeTest: includeTest ? "true" : "false",
                excludeStaff: excludeStaff ? "true" : "false",
            };
            if (start) params.start = start;
            if (end) params.end = end;
            if (language) params.language = language;
            return await ApiService.getData("/v1/stats/jigsaw/user-plays", params);
        } catch (error) {
            Logger.error("Error fetching jigsaw user play stats:", error);
            throw error;
        }
    },

    /**
     * GET /v1/stats/jigsaw/by-difficulty?language=english
     * Board counts per tile size and difficulty, with per-state and free/paid breakdown.
     * @returns {Promise<{ groups: Array, byState: Object, totalBoards: number, language: string }>}
     */
    statsByDifficulty: async (language = "english") => {
        try {
            const params = {};
            if (language) params.language = language;
            return await ApiService.getData("/v1/stats/jigsaw/by-difficulty", params);
        } catch (error) {
            Logger.error("Error fetching jigsaw stats by difficulty:", error);
            throw error;
        }
    },

    /**
     * GET /v1/stats/jigsaw/color-baskets?start=&end=&language=&includeTest=false&excludeStaff=true
     * Color-basket sessions, complete ratio per basket and puzzle size, free vs paid.
     * Start / end are epoch seconds. Omit or send a far-past start for all-time.
     */
    colorBasketStats: async ({
        start,
        end,
        language = "english",
        includeTest = false,
        excludeStaff = true,
    } = {}) => {
        try {
            const params = {
                includeTest: includeTest ? "true" : "false",
                excludeStaff: excludeStaff ? "true" : "false",
            };
            if (start) params.start = start;
            if (end) params.end = end;
            if (language) params.language = language;
            return await ApiService.getData("/v1/stats/jigsaw/color-baskets", params);
        } catch (error) {
            Logger.error("Error fetching jigsaw color basket stats:", error);
            throw error;
        }
    },

    /**
     * GET /v1/stats/jigsaw/tags?language=english
     * Per-tag REVIEWED vs PUBLISHED story-bundle counts, play sessions, and last published time.
     */
    statsByTag: async (language = "english") => {
        try {
            const params = {};
            if (language) params.language = language;
            return await ApiService.getData("/v1/stats/jigsaw/tags", params);
        } catch (error) {
            Logger.error("Error fetching jigsaw tag stats:", error);
            throw error;
        }
    },

    /**
     * GET /v1/stats/jigsaw/top-users?language=&page=&pageSize=&includeTest=&excludeStaff=&notifications=
     * Highest jigsaw score first (leaderboard + guest completed counts).
     */
    topUsers: async ({
        language = "english",
        page = 0,
        pageSize = 25,
        includeTest = false,
        excludeStaff = true,
        notifications = "all",
    } = {}) => {
        try {
            const params = {
                page,
                pageSize,
                includeTest: includeTest ? "true" : "false",
                excludeStaff: excludeStaff ? "true" : "false",
            };
            if (language) params.language = language;
            if (notifications) params.notifications = notifications;
            return await ApiService.getData("/v1/stats/jigsaw/top-users", params);
        } catch (error) {
            Logger.error("Error fetching jigsaw top users:", error);
            throw error;
        }
    },

    /**
     * GET /v1/stats/jigsaw/promotion-users?language=&page=&pageSize=&includeTest=&excludeStaff=&excludeNoEmail=&keyword=
     * Signed-in players with play counts and first/last play times.
     */
    promotionUsers: async ({
        language = "english",
        page = 0,
        pageSize = 25,
        includeTest = false,
        excludeStaff = true,
        excludeNoEmail = false,
        keyword = "",
    } = {}) => {
        try {
            const params = {
                page,
                pageSize,
                includeTest: includeTest ? "true" : "false",
                excludeStaff: excludeStaff ? "true" : "false",
                excludeNoEmail: excludeNoEmail ? "true" : "false",
            };
            if (language) params.language = language;
            if (keyword) params.keyword = keyword;
            return await ApiService.getData("/v1/stats/jigsaw/promotion-users", params);
        } catch (error) {
            Logger.error("Error fetching jigsaw promotion users:", error);
            throw error;
        }
    },

    /**
     * GET /v1/stats/jigsaw/player-plays?cloudUserId=&sessionId=&start=&end=&language=
     * Same shape as user-plays for one signed-in player or guest session.
     */
    playerPlayStats: async ({
        cloudUserId,
        sessionId,
        start,
        end,
        language = "english",
    } = {}) => {
        try {
            const params = {};
            if (cloudUserId) params.cloudUserId = cloudUserId;
            if (sessionId) params.sessionId = sessionId;
            if (start) params.start = start;
            if (end) params.end = end;
            if (language) params.language = language;
            return await ApiService.getData("/v1/stats/jigsaw/player-plays", params);
        } catch (error) {
            Logger.error("Error fetching jigsaw player play stats:", error);
            throw error;
        }
    },

    /**
     * GET /v1/stats/jigsaw/top-puzzles?language=&page=&pageSize=&includeTest=&excludeStaff=&start=&end=
     * Stories ranked by play count. Omit start/end for all time.
     * Pass editorsPicksOnly=true to limit to editor's-picks stories.
     */
    topPuzzles: async ({
        language = "english",
        page = 0,
        pageSize = 25,
        includeTest = false,
        excludeStaff = true,
        editorsPicksOnly = false,
        start,
        end,
        sortField = "completedPlays",
        sortDir = "desc",
    } = {}) => {
        try {
            const params = {
                page,
                pageSize,
                includeTest: includeTest ? "true" : "false",
                excludeStaff: excludeStaff ? "true" : "false",
                editorsPicksOnly: editorsPicksOnly ? "true" : "false",
                sortField,
                sortDir,
            };
            if (language) params.language = language;
            if (start) params.start = start;
            if (end) params.end = end;
            return await ApiService.getData("/v1/stats/jigsaw/top-puzzles", params);
        } catch (error) {
            Logger.error("Error fetching jigsaw top puzzles:", error);
            throw error;
        }
    },
};

export default StatsJigsawService;
